import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles, Typography, Button } from '@material-ui/core';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import ContentHeader from './ContentHeader';
import LoaderButton from './LoaderButton';
import ButtonLink from './ButtonLink';

const styles = {
    root: {
        textAlign: 'left',
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)'
    },
    description: {
        padding: '10px',
        marginBottom: '20px',
        fontWeight: '100'
    },
    button: {
        height: '40px',
        marginTop: '10px',
        marginBottom: '10px'
    },
    passengerInfo: {
        padding: '10px',
        marginBottom: '20px',
        textAlign: 'center'
    },
    imagePreview: {
        minHeight: '56px',
        margin: '20px auto 0',
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        justifyContent: 'center',
        '& > img': {
            maxWidth: '100%'
        },
        '@media only screen and (max-device-width: 400px)': {
            margin: '0 auto 0'
        }
    },
    tripLine: {
        border: '1px solid #ccc',
        borderRadius: 5,
        marginBottom: 5,
        textAlign: 'left',
        padding: 5,
        display: 'flex'
    },
    tripLineHead: {
        display: 'flex',
        borderRight: '1px solid #ccc',
        paddingRight: 5,
        marginRight: 5,
        textAlign: 'center',
        width: 55,
        justifyContent: 'center'
    },
    tripDate: {
        fontWeight: 'bold'
    },
    tripDateDay: {
        fontSize: 20,
        display: 'block',
        color: '#5dcaff'
    },
    tripDateMonth: {
        fontSize: 8,
        display: 'block'
    },
    tripService: {
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'center',
        flexDirection: 'column'
    },
    tripStop: {
        fontSize: '0.8em',
        color: '#666',
        display: 'flex',
        alignItems: 'center'
    },
    icon: {
        fontSize: '15px'
    },
    buyBtn: {
        background: 'green !important',
        color: 'white  !important',
        border: 'none',
        marginLeft: 8,
        '&:hover': {
            background: 'green',
            color: 'white',
            border: 'none'
        }
    }
};

class ConfirmBookingChange extends Component {
    constructor(props) {
        super(props);

        this.state = {
            voucher: null,
            isLoading: false,
            error: null,
            hasSwapped: false
        };
    }

    netPrice = price => {
        const { voucher } = this.state;
        if (voucher) {
            return price - voucher.value;
        }
        return price;
    };

    async handleSubmit() {
        try {
            this.setState({
                isLoading: true,
                error: null
            });

            const currentSession = await Auth.currentSession();

            // get customer ticket
            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const { booking, oldBooking } = this.props;

            const bookingItem = booking[0];

            await axios.post(
                `${process.env.REACT_APP_BOOKING_API_ENDPOINT}/booking/swap`,
                {
                    old_booking: {
                        pk: oldBooking.pk,
                        sk: oldBooking.sk
                    },
                    new_booking: {
                        service_id: bookingItem.service_id,
                        service_name: bookingItem.service_name,
                        date: bookingItem.date,
                        time: bookingItem.time,
                        price: bookingItem.price,
                        stop: bookingItem.stop
                    }
                },
                {
                    headers
                }
            );

            this.setState({
                isLoading: false,
                hasSwapped: true
            });
        } catch (e) {
            console.error(e);

            let errMessage =
                e.response && e.response.status === 403
                    ? 'You do not have permission to use this ticket. Please log into the original account which purchased the ticket. Hint: Please use the email address where your booking confirmation was sent to.'
                    : 'There was a problem retrieving your pass. Please try again later or contact our support team.';

            if (e.response && e.response.data && e.response.data.error) {
                errMessage = e.response.data.error;
            }

            this.setState({
                isLoading: false,
                error: errMessage
            });
        }
    }

    renderTrip(booking) {
        const { classes } = this.props;
        const item = booking[0];

        return (
            <>
                <div key={item} className={classes.tripLine}>
                    <div className={classes.tripLineHead}>
                        <div className={classes.tripDate}>
                            <span className={classes.tripDateDay}>
                                {moment(item.date)
                                    .format('ddd')
                                    .toUpperCase()}
                            </span>
                            <span className={classes.tripDateMonth}>
                                {moment(item.date)
                                    .format('Do MMM')
                                    .toUpperCase()}
                            </span>
                        </div>
                    </div>
                    <div className={classes.tripService}>
                        {item.time} | {item.service_name}
                        {'stop' in item && (
                            <div className={classes.tripStop}>
                                <span className={classes.tripStopLabel}>
                                    {item.stop}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }

    render() {
        const { handleEdit, booking, oldBooking, classes } = this.props;
        const { isLoading, hasSwapped, error } = this.state;
        const ticketId = oldBooking.ctid;

        if (hasSwapped) {
            return (
                <div>
                    <div>
                        <div className={classes.root}>
                            <ContentHeader
                                text="Journey changed"
                                id="trip-details-heading"
                            />
                            <div className={classes.passengerInfo}>
                                <Typography
                                    variant="body2"
                                    style={{ marginBottom: 10 }}
                                >
                                    Booking successfully updated.
                                </Typography>
                                <Typography
                                    variant="body2"
                                    style={{ marginBottom: 10 }}
                                >
                                    An email will confirm your updated booking
                                    and you can view all bookings from the My
                                    Tickets area.
                                </Typography>
                                <ButtonLink
                                    className={classes.button}
                                    variant="outlined"
                                    color="primary"
                                    to={`/ticket/${ticketId}/bookings`}
                                >
                                    View bookings
                                </ButtonLink>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <div>
                    <div className={classes.root}>
                        <ContentHeader
                            text="Confirm change"
                            id="trip-details-heading"
                        />
                        <div className={classes.passengerInfo}>
                            <Typography
                                variant="body2"
                                style={{ marginBottom: 10 }}
                            >
                                Changing journey to the following:
                            </Typography>
                            {this.renderTrip(booking)}
                            {error && (
                                <Typography
                                    variant="body2"
                                    style={{ color: 'red', marginBottom: 10 }}
                                >
                                    {error}
                                </Typography>
                            )}
                            <Button
                                variant="outlined"
                                color="primary"
                                className={classes.button}
                                onClick={handleEdit}
                                disabled={isLoading}
                            >
                                Go back
                            </Button>
                            <LoaderButton
                                className={`${classes.button} ${classes.buyBtn}`}
                                variant="outlined"
                                color="primary"
                                type="submit"
                                disabled={isLoading}
                                isLoading={isLoading}
                                text="Confirm"
                                onClick={() => {
                                    this.handleSubmit();
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

ConfirmBookingChange.propTypes = {
    handleEdit: PropTypes.func.isRequired,
    passenger: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        photo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        stop: PropTypes.string
    }).isRequired,
    classes: PropTypes.shape().isRequired
};

export default withStyles(styles)(ConfirmBookingChange);
